import _ from 'lodash';
import { useCallback } from 'react';
import { ADMIN_LIMIT } from '@/administration/administration.constants';
import { useAdministrationActions } from '@/administration/administration.store';
import { useAdministrationRequest } from '@/administration/useAdministrationRequest.hook';
import { sqExportApi } from '@/sdk/api/ExportApi';
import { sqJobsApi } from '@/sdk/api/JobsApi';
import { sqRequestsApi } from '@/sdk/api/RequestsApi';
import { sqSharingApi } from '@/sdk/api/SharingApi';
import { sqSubscriptionsApi } from '@/sdk/api/SubscriptionsApi';
import { sqSystemApi } from '@/sdk/api/SystemApi';
import { sqUserGroupsApi } from '@/sdk/api/UserGroupsApi';
import { isExternalOrganizationTabEnabled } from '@/services/systemConfiguration.utilities';
import { errorToast, successToast, warnToast } from '@/utilities/toast.utilities';
import type { ChannelOutputV1 } from '@/sdk/model/ChannelOutputV1';
import type { JobOutputV1 } from '@/sdk/model/JobOutputV1';
import type { OrganizationOutputV1 } from '@/sdk/model/OrganizationOutputV1';
import type { RequestOutputV1 } from '@/sdk/model/RequestOutputV1';
import type { UserGroupOutputV1 } from '@/sdk/model/UserGroupOutputV1';

export function useGroupsQuery(enabled = true) {
  const { clearGroups, setGroups } = useAdministrationActions();
  const requestFn = useCallback(async () => {
    clearGroups();
    const { data } = await sqUserGroupsApi.getUserGroups({ limit: ADMIN_LIMIT });
    const groups = data.items ?? [];
    setGroups(groups);
    return groups;
  }, [clearGroups, setGroups]);

  return useAdministrationRequest<UserGroupOutputV1[]>({
    enabled,
    initialData: [],
    requestFn,
  });
}

export function useRemoveGroupsMutation() {
  const { removeGroups } = useAdministrationActions();
  return useCallback(
    async (ids: string[]): Promise<void> => {
      if (_.isEmpty(ids)) {
        warnToast({ messageKey: 'ADMIN.GROUP.SELECTION_REQUIRED' });
        return;
      }

      try {
        await Promise.all(ids.map((userGroupId) => sqUserGroupsApi.removeUserGroup({ userGroupId })));
        removeGroups(ids);
        successToast({ messageKey: 'ADMIN.GROUP.REMOVED' });
      } catch (error) {
        errorToast({ httpResponseOrError: error, displayForbidden: true });
        throw error;
      }
    },
    [removeGroups],
  );
}

export function useAdminContactInfoQuery(enabled = true) {
  const { setAdminContactInfo } = useAdministrationActions();
  const requestFn = useCallback(async () => {
    const { data } = await sqSystemApi.getAdministratorContactInformation();
    const adminContactInfo = _.pick(data, ['name', 'email']) as { name: string; email: string };
    setAdminContactInfo(adminContactInfo);
    return adminContactInfo;
  }, [setAdminContactInfo]);

  return useAdministrationRequest<{ name: string; email: string }>({
    enabled,
    initialData: { name: '', email: '' },
    requestFn,
  });
}

export function useUpdateAdminContactInfoMutation() {
  const { setAdminContactInfo } = useAdministrationActions();
  return useCallback(
    async (adminContactInfo: { name: string; email: string }) => {
      await sqSystemApi.setAdministratorContactInformation(adminContactInfo);
      setAdminContactInfo(adminContactInfo);
    },
    [setAdminContactInfo],
  );
}

type RequestsQueryData = {
  requests: RequestOutputV1[];
};

export function useRequestsQuery() {
  const { setRequests, setTooManyRequests } = useAdministrationActions();
  const requestFn = useCallback(async () => {
    const { data } = await sqRequestsApi.getRequests({ limit: ADMIN_LIMIT });
    const requests = data.requests ?? [];
    setRequests(requests);
    setTooManyRequests(requests.length >= ADMIN_LIMIT);
    return { requests };
  }, [setRequests, setTooManyRequests]);

  return useAdministrationRequest<RequestsQueryData>({
    initialData: { requests: [] },
    requestFn,
  });
}

export function useCancelRequestsMutation() {
  return useCallback(async (ids: string[]) => {
    await Promise.all(ids.map((requestId) => sqRequestsApi.cancelRequest({ requestId })));
  }, []);
}

type SubscriptionsQueryData = {
  channels: ChannelOutputV1[];
};

export function useSubscriptionsQuery() {
  const { setSubscriptions } = useAdministrationActions();
  const requestFn = useCallback(async () => {
    const { data } = await sqSubscriptionsApi.getChannels({ limit: ADMIN_LIMIT });
    const channels = data.channels ?? [];
    setSubscriptions(channels);
    return { channels };
  }, [setSubscriptions]);

  return useAdministrationRequest<SubscriptionsQueryData>({
    initialData: { channels: [] },
    requestFn,
  });
}

type JobsQueryData = {
  jobs: JobOutputV1[];
};

export function useJobsQuery() {
  const { setJobs } = useAdministrationActions();
  const requestFn = useCallback(async () => {
    const { data } = await sqJobsApi.getJobs({ limit: ADMIN_LIMIT });
    const jobs = data.jobs ?? [];
    setJobs(jobs);
    return { jobs };
  }, [setJobs]);

  return useAdministrationRequest<JobsQueryData>({
    initialData: { jobs: [] },
    requestFn,
  });
}

export function useStopJobsMutation() {
  return useCallback(async (jobs: Array<{ id: string; group: string }>) => {
    await Promise.all(jobs.map((job) => sqJobsApi.deleteJob(job)));
  }, []);
}

export function useArchiveExportsMutation() {
  return useCallback(async (ids: string[]): Promise<void> => {
    await Promise.all(ids.map((id) => sqExportApi.archiveExport({ id })));
    successToast({ messageKey: 'ADMIN.EXPORT.TRASHED' });
  }, []);
}

export function useInitializeExternalOrganizationsQuery(enabled = true) {
  const { setExternalOrganizations } = useAdministrationActions();
  const requestFn = useCallback(async () => {
    if (!isExternalOrganizationTabEnabled()) {
      setExternalOrganizations([]);
      return [];
    }

    try {
      const { data } = await sqSharingApi.getOrganizations({ limit: 1, offset: 0, isArchived: false }, {});
      const organizations = data.organizations ?? [];
      setExternalOrganizations(organizations);
      return organizations;
    } catch {
      setExternalOrganizations([]);
      return [];
    }
  }, [setExternalOrganizations]);

  return useAdministrationRequest<OrganizationOutputV1[]>({
    enabled,
    initialData: [],
    requestFn,
  });
}
